/* eslint-disable no-unused-expressions */
import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { useValidation } from "../utils/ValidationProvider";
import "./NumericTextBox.scss";

/**
 * Numeric text box component
 * @module
 * @param {string} name - Name
 * @param {func} onChange - Function invoked on value change
 * @param {number} [value] - Current value
 * @param {bool} [required] - Is input required?
 * @param {bool} [disabled] - Is input readonly?
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {number} [min] - Minimum number
 * @param {number} [max] - Maximum number
 * @param {number} [step] - Step
 * @param {(string | object)} [format] - Format
 * @param {bool} [autofocus] - Should input be focused?
 * @param {func} [onBlur] - Function invoked on input blur
 * @param {number} [tabIndex] - NumericTextBox Tabindex
 */

const NumericTextBoxComponent = (props) => {
  const ref = useRef(null);

  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);

  const { validate, setValidationValue } = useValidation(field, props.value);

  useEffect(() => {
    //disable change value with mouse wheel
    const input = ref.current?.element;
    if(input) {
      const handleWheel = (e) => {
        if (input && input.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
  
      input.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        input.removeEventListener('wheel', handleWheel, { passive: false });
      };
    }
  }, []);

  useEffect(() => {
    if (props.autofocus && ref.current) {
      const input = ref.current.querySelector('input');
      if (input) {
        input.focus();
      }
    }
  }, [props.autofocus]);

  const onBlur = () => {
    validate();
    if (props.onBlur) {
      props.onBlur(props.name, props.value);
    }
  };

  return (
    <>
      <NumericTextBox
        data-testid={props.name || props.title}
        className={`form-input numerictextbox ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")} ${props.className ? props.className : ""}`}
        onChange={(e) => { props.onChange(props.name, e.target.value); setValidationValue(e.target.value); }}
        value={props.value}
        required={props.required}
        validationMessage={props.validationMessage || "Povinné"}
        // disabled={props.disabled}
        readOnly={props.disabled}
        min={props.min === null ? undefined : props.min}
        max={props.max === null ? undefined : props.max}
        step={props.step === null ? undefined : props.step}
        format={props.format || undefined}
        ref={ref} //TODO: preco v starej verzii bolo: ref="numerictextbox"
        tabIndex={props.tabIndex}
        onBlur={onBlur}
        placeholder={props.placeholder}
        spinners={props.spinners}
      />
    </>
  );
};

NumericTextBoxComponent.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.number,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  validationMessage: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  format: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  autofocus: PropTypes.bool,
  onBlur: PropTypes.func,
  tabIndex: PropTypes.number,
  spinners: PropTypes.bool,
};

export default NumericTextBoxComponent;